import {
  filledByType,
  IGroupInfo,
  ImmutableChat,
  SERVICE_STATUS,
  USER_TYPE,
} from "@home-health-notify/domain";
import {
  OrganizationContext,
  patientServiceColor,
  useTranslation,
} from "hhn-commons";
import { useContext, useEffect, useMemo, useState } from "react";
import CommonDialog from "../../common/CommonDialog/CommonDialog";
import {
  CancelButton,
  RoleLabel,
  RolesContainer,
  StyledRadio,
  TemplateAccessWrapper,
} from "./TemplateAccess.style";
import TemplateMessage from "./TemplateMessage";

interface TemplateAccessProps {
  chat: ImmutableChat;
  isOpen: boolean;
  onClose: () => void;
}

const TemplateAccess = ({ chat, isOpen, onClose }: TemplateAccessProps) => {
  const { selectedOrgUser } = useContext(OrganizationContext);
  const loggedInUserType =
    selectedOrgUser?.getOrganization().userProfile.type ?? "";
  const { t } = useTranslation();
  const tNs = (key: string): string => t(key, { ns: "message" });

  const servicesToPatient = useMemo(() => {
    const services = (chat?.chatInfo as IGroupInfo)?.servicesToPatient;
    return (services || []).filter(
      service => service.status !== SERVICE_STATUS.END_OF_CARE,
    );
  }, [chat?.chatInfo]);

  const [isTemplateAccessDialogOpen, setTemplateAccessDialogOpen] =
    useState<boolean>(false);
  const [isTemplateMessageDialogOpen, setTemplateMessageDialogOpen] =
    useState<boolean>(false);
  const [selectedRole, setSelectedRole] = useState<filledByType | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedRole(e.target.value as filledByType);
  };

  const reset = () => {
    setTemplateAccessDialogOpen(false);
    setTemplateMessageDialogOpen(false);
  };

  useEffect(() => {
    if (isOpen) {
      setSelectedRole(null);
      if (
        loggedInUserType === USER_TYPE.ADMIN ||
        loggedInUserType === USER_TYPE.STAFF
      ) {
        setTemplateAccessDialogOpen(true);
      } else {
        setTemplateMessageDialogOpen(true);
      }
    } else {
      reset();
    }
  }, [isOpen, loggedInUserType]);

  useEffect(() => {
    if (selectedRole) {
      setTemplateMessageDialogOpen(true);
      setTemplateAccessDialogOpen(false);
    }
  }, [selectedRole]);

  return (
    <>
      <CommonDialog
        isDialogOpen={isTemplateAccessDialogOpen}
        onDialogClose={onClose}
        maxWidth="xs"
        title={tNs("templateAccess")}
        titleAlignment="center"
      >
        <TemplateAccessWrapper>
          <RolesContainer value={selectedRole ?? ""} onChange={handleChange}>
            <RoleLabel
              value={loggedInUserType}
              control={<StyledRadio />}
              label={loggedInUserType}
            />
            {servicesToPatient?.map(service => (
              <RoleLabel
                key={service.shorthand}
                value={service.shorthand}
                control={
                  <StyledRadio
                    data-testid="role"
                    statusColor={patientServiceColor(service.status)}
                  />
                }
                label={service.shorthand}
                statusColor={patientServiceColor(service.status)}
              />
            ))}
          </RolesContainer>
          <CancelButton variant="outlined" onClick={onClose}>
            {t("buttonLabels.cancel")}
          </CancelButton>
        </TemplateAccessWrapper>
      </CommonDialog>
      <TemplateMessage
        isOpen={isTemplateMessageDialogOpen}
        onClose={onClose}
        selectedRole={selectedRole ?? undefined}
        chat={chat}
      />
    </>
  );
};

export default TemplateAccess;
